// src/store/store.ts

import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { baseApi } from '../api/baseApi';
import { errorTrackingApi } from '../api/errorTrackingApi';
import { centralAuthApi } from '../api/centralAuthApi';
import { centralBillingApi } from '../api/centralBillingApi';
import { centralMembersApi } from '../api/centralMembersApi';
import { centralNotificationsApi } from '../api/centralNotificationsApi';
import { centralTestingModeApi } from '../api/centralTestingModeApi';
import layoutReducer from './slices/layoutSlice';
import authReducer from './slices/authSlice';
import centralAuthReducer from './slices/centralAuthSlice';
import bootContextReducer from './slices/bootContextSlice';
import featureFlagsReducer from './slices/featureFlagsSlice';

export const store = configureStore({
  reducer: {
    [baseApi.reducerPath]: baseApi.reducer,
    [errorTrackingApi.reducerPath]: errorTrackingApi.reducer,
    [centralAuthApi.reducerPath]: centralAuthApi.reducer,
    [centralBillingApi.reducerPath]: centralBillingApi.reducer,
    [centralMembersApi.reducerPath]: centralMembersApi.reducer,
    [centralNotificationsApi.reducerPath]: centralNotificationsApi.reducer,
    [centralTestingModeApi.reducerPath]: centralTestingModeApi.reducer,
    layout: layoutReducer,
    auth: authReducer,
    centralAuth: centralAuthReducer,
    bootContext: bootContextReducer,
    featureFlags: featureFlagsReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(baseApi.middleware)
      .concat(errorTrackingApi.middleware)
      .concat(centralAuthApi.middleware)
      .concat(centralBillingApi.middleware)
      .concat(centralMembersApi.middleware)
      .concat(centralNotificationsApi.middleware)
      .concat(centralTestingModeApi.middleware),
});

// Enable refetchOnFocus / refetchOnReconnect for endpoints that opt in
// (the Contacts page does — see pages/ContactsPage.tsx). Without this,
// those options are silent no-ops, so cross-app contact changes synced
// in via the realtime hub OR via the internal endpoints would never
// reach a tab whose WebSocket happens to be disconnected.
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
